import React, { useEffect, useState } from "react";
import { Modal, Button } from "antd";
import { useLoader } from "../../../../Context/LoaderContext";
import zohoRecruitService from "../../../../Service/zoho.recruit.service";
import { openNotification } from "../../../../Components/Notification";
import bulkUploadService from "../../../../Service/bulk.upload.service";
import { JobDescriptionEntity } from "../../../../Entities/JobDescription";
import { ATSCandidate } from "../../../../Interface";
import { useLocation, useNavigate } from "react-router-dom";
import "./ATSImportModal.scss";

interface ATSImportModalProps {
  selectedATS: "zoho" | "lever" | "custom";
  open: boolean;
  onClose: () => void;
  jobPost: JobDescriptionEntity;
}

const ATSImportModal: React.FC<ATSImportModalProps> = ({
  selectedATS,
  open,
  onClose,
  jobPost,
}) => {
  const [candidates, setCandidates] = useState<ATSCandidate[]>([]);
  const [selected, setSelected] = useState<ATSCandidate[]>([]);
  const { setLoading, setLoadingMessage } = useLoader();
  const location = useLocation();
  const navigate = useNavigate();

  const getTitle = () => {
    if (selectedATS === "zoho") {
      return "Import candidates from Zoho Recruit";
    } else {
      return "Import candidates from Lever Hire";
    }
  };

  const handleATSPull = async () => {
    if (selected.length === 0) {
      openNotification(
        "error",
        "Please atleast select one candidate to begin",
        "",
      );
      return;
    }
    try {
      setLoading(true);
      setLoadingMessage("Processing...");
      const response = await bulkUploadService.pullATSCandidates(
        jobPost._id,
        selected,
        selectedATS,
      );
      if (response) {
        setLoading(false);
        setLoadingMessage(null);
        openNotification("success", "processing started", "");
        const searchParams = new URLSearchParams(location.search);
        searchParams.set("tabIndex", "bulk-uploads");

        navigate(`${location.pathname}?${searchParams.toString()}`, {
          replace: true,
        });
        onClose();
        return;
      } else {
        setLoading(false);
        setLoadingMessage(null);
        openNotification("error", "failed to process", "");
        onClose();
      }
    } catch (error: any) {
      console.error("🚀 ~ handleATSPull ~ error:", error);
      openNotification("error", "Failed to pull", "");
    } finally {
      setLoading(false);
      setLoadingMessage(null);
    }
  };

  useEffect(() => {
    const init = async () => {
      if (selectedATS === "zoho" && jobPost && jobPost.appJobId) {
        setLoading(true);
        const data = await zohoRecruitService.getAllOpportunities(jobPost.appJobId);
        setCandidates(data ?? []);
        setLoading(false);
      } else {
        // TODO :lever hire
        return;
      }
    };

    init();
  }, [selectedATS,jobPost]);

  return (
    <Modal
      open={open}
      maskClosable={false}
      keyboard={false}
      onCancel={onClose}
      title={getTitle()}
      centered
      width={800}
      footer={null}
    >
      <div className="ats-import-modal__list">
        {candidates.map((candidate) => {
          const isSelected = selected.some(
            (c) => c.atsApplicationId === candidate.atsApplicationId,
          );
          const isDisabled = candidate.isAlreadyApplied;
          const toggleSelect = () => {
            if (isDisabled) return;
            setSelected((prev) =>
              isSelected
                ? prev.filter(
                    (c) => c.atsApplicationId !== candidate.atsApplicationId,
                  )
                : [...prev, candidate],
            );
          };
          return (
            <div
              key={candidate.atsApplicationId}
              className={`ats-import-modal__item${isSelected ? " ats-import-modal__item--selected" : ""}${isDisabled ? " ats-import-modal__item--disabled" : ""}`}
              onClick={toggleSelect}
            >
              <div className="ats-import-modal__info">
                <div className="ats-import-modal__avatar">
                  {candidate.fullName.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="ats-import-modal__name-row">
                    <p className="ats-import-modal__name">
                      {candidate.fullName}
                    </p>
                    {isDisabled && (
                      <span className="ats-import-modal__badge">Applied</span>
                    )}
                  </div>
                  <p className="ats-import-modal__email">{candidate.email}</p>
                  {candidate.mobile && (
                    <p className="ats-import-modal__phone">{candidate.mobile}</p>
                  )}
                </div>
              </div>
              <input
                type="checkbox"
                className="ats-import-modal__checkbox"
                checked={isSelected}
                disabled={isDisabled}
                onChange={toggleSelect}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          );
        })}
      </div>
      <div className="ats-import-modal__footer">
        <span className="ats-import-modal__selected-count">
          {selected.length} selected
        </span>
        <Button
          type="primary"
          disabled={selected.length === 0}
          onClick={handleATSPull}
        >
          Pull
        </Button>
      </div>
    </Modal>
  );
};

export default ATSImportModal;
